import React from 'react';
import { Box, Typography, Container, Grid, Card, CardContent, CardMedia, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { MapPin, ChevronLeft, Instagram, ExternalLink } from 'lucide-react';

const INSTAGRAM_HANDLE = 'https://www.instagram.com/revivingindia/';

const WORKSHOPS = [
  {
    id: 'meerut',
    location: 'Meerut Institute of Technology',
    instagramUrl: 'https://www.instagram.com/reels/DVq-wxekttl/',
    shortDesc: 'Hands-on workshop on practical skills and real-world knowledge. Learn from experts and connect with peers.',
    image: '/beyond/live-events.jpg',
  },
  {
    id: 'vidhaya-peeth',
    location: 'Vidhaya Peeth',
    instagramUrl: 'https://www.instagram.com/reels/DVRTUq_knE1/',
    shortDesc: 'Skills, creativity, and digital content. Build confidence and understand strategies that work in the real world.',
    image: '/beyond/beyond-gallery.jpg',
  },
  {
    id: 'rdec-ghaziabad',
    location: 'RDEC Ghaziabad',
    instagramUrl: 'https://www.instagram.com/reels/DVG5L3pkoVi/',
    shortDesc: 'Practical techniques for growth and success. Focus on smart work, consistency, and creative thinking.',
    image: '/beyond/internship-hub.jpg',
  },
];

const LiveEventsPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 8, md: 10 }, pb: 8, bgcolor: '#F8FAFC' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          color: '#fff',
          py: { xs: 6, md: 8 },
          px: 2,
          mb: 6,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(/beyond/live-events.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.2,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Button
              startIcon={<ChevronLeft size={18} />}
              onClick={() => navigate('/')}
              sx={{
                color: 'rgba(255,255,255,0.9)',
                textTransform: 'none',
                fontWeight: 600,
                mb: 2,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Back to Home
            </Button>
            <Typography sx={{ color: '#93c5fd', fontWeight: 600, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
              Live Events & Programs
            </Typography>
            <Typography component="h1" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, fontWeight: 800, mb: 2, lineHeight: 1.2 }}>
              Workshop Highlights
            </Typography>
            <Typography sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, color: 'rgba(255,255,255,0.9)', lineHeight: 1.8, maxWidth: 700 }}>
              Knowledge-packed sessions, competitions, and meetups. Watch reels from our recent workshops and follow for upcoming events.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ px: 2 }}>
        <Typography component="h2" sx={{ fontSize: { xs: '1.5rem', md: '1.75rem' }, fontWeight: 700, color: '#1e293b', mb: 3 }}>
          Recent Workshops
        </Typography>

        <Grid container spacing={4}>
          {WORKSHOPS.map((workshop, i) => (
            <Grid size={{ xs: 12, md: 4 }} key={workshop.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <Card
                  sx={{
                    height: '100%',
                    overflow: 'hidden',
                    borderRadius: '20px',
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 20px 40px rgba(228, 64, 95, 0.12)',
                      borderColor: '#E4405F',
                    },
                  }}
                >
                  <Box sx={{ position: 'relative', height: 180, overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      image={workshop.image}
                      alt={workshop.location}
                      sx={{
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.4s ease',
                        '&:hover': { transform: 'scale(1.05)' },
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.75), transparent 50%)',
                        display: 'flex',
                        alignItems: 'flex-end',
                        p: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <MapPin size={18} color="#fff" />
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
                          {workshop.location}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography sx={{ color: '#64748b', mb: 2, lineHeight: 1.6, fontSize: '0.95rem' }}>
                      {workshop.shortDesc}
                    </Typography>
                    <Typography
                      component="a"
                      href={workshop.instagramUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'block',
                        fontSize: '0.8rem',
                        color: '#E4405F',
                        mb: 2,
                        textDecoration: 'none',
                        wordBreak: 'break-all',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {workshop.instagramUrl}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Button
                        component="a"
                        href={workshop.instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="contained"
                        size="small"
                        startIcon={<ExternalLink size={16} />}
                        sx={{
                          bgcolor: '#E4405F',
                          color: '#fff',
                          fontWeight: 600,
                          textTransform: 'none',
                          px: 2,
                          py: 0.75,
                          borderRadius: '10px',
                          '&:hover': { bgcolor: '#C13584' },
                        }}
                      >
                        View Instagram
                      </Button>
                      <Button
                        component="a"
                        href={INSTAGRAM_HANDLE}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        size="small"
                        startIcon={<Instagram size={16} />}
                        sx={{
                          borderColor: '#E4405F',
                          color: '#E4405F',
                          fontWeight: 600,
                          textTransform: 'none',
                          px: 2,
                          py: 0.75,
                          borderRadius: '10px',
                          '&:hover': {
                            borderColor: '#C13584',
                            color: '#C13584',
                            bgcolor: 'rgba(228, 64, 95, 0.06)',
                          },
                        }}
                      >
                        Follow
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Follow CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Box
            sx={{
              mt: 6,
              textAlign: 'center',
              py: 5,
              px: 2,
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #E4405F 0%, #C13584 100%)',
              color: '#fff',
            }}
          >
            <Instagram size={40} style={{ marginBottom: 12, opacity: 0.95 }} />
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, mb: 1 }}>
              Stay updated with workshops & events
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.9)', mb: 2, fontSize: '0.95rem' }}>
              Follow @revivingindia for reels, highlights, and upcoming sessions.
            </Typography>
            <Button
              component="a"
              href={INSTAGRAM_HANDLE}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              startIcon={<Instagram size={20} />}
              sx={{
                bgcolor: '#fff',
                color: '#E4405F',
                fontWeight: 700,
                textTransform: 'none',
                py: 1.25,
                px: 4,
                borderRadius: '12px',
                '&:hover': { bgcolor: '#f8fafc', color: '#C13584' },
              }}
            >
              Follow on Instagram
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default LiveEventsPage;
